import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { Icon } from "@iconify/react";
import { inferSchema } from "../../utils/schemaInference";

// Export the dataset's item metadata schema.
//
// Mirrors the data item dialog's "Export schema" action: the schema is shown as JSON with a copy
// action. When the dataset has no schema yet, one is inferred from its metadata so there is
// always something to export (and to use as the dataset schema).

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000000002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 720px;
  max-height: 85vh;
  overflow: auto;
  padding: 16px;
  border-radius: 10px;
  background-color: var(--dialog-bg);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.35);
  color: var(--main-font-color);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: var(--main-font-color);
`;

const Hint = styled.div`
  font-size: 12.5px;
  color: #a0a6b1;
  line-height: 1.5;
`;

const Code = styled.textarea`
  width: 100%;
  min-height: 240px;
  padding: 9px 11px;
  font-family: monospace;
  font-size: 12px;
  line-height: 1.5;
  color: var(--main-font-color);
  background-color: var(--main-bg);
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  resize: vertical;
  box-sizing: border-box;
`;

const Row = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  font-size: 12.5px;
  border: 1px solid var(--main-border-color);
  border-radius: 6px;
  background: transparent;
  color: var(--main-font-color);
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const Primary = styled(Button)`
  border: none;
  background-color: var(--button-bg);
  color: var(--button-font-color);
  font-weight: 600;
`;

const Notice = styled.div`
  font-size: 12.5px;
  color: #2ecc71;
`;

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
};

/**
 * @param {object} props
 * @param {boolean} props.show
 * @param {object|string|null} props.schema The dataset's current schema.
 * @param {*} [props.metadata] The dataset's metadata, used to infer a schema when none exists.
 * @param {string} [props.name] Dataset name, for the download filename.
 * @param {Function} [props.onClose]
 * @param {(schema: object) => void} [props.onUseSchema]
 */
export const SchemaExportDialog = ({
  show = false,
  schema = null,
  metadata = null,
  name = "dataset",
  onClose = () => {},
  onUseSchema = null,
}) => {
  const [copied, setCopied] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    if (show) {
      setCopied(false);
      setApplied(false);
    }
  }, [show]);

  const inferred = useMemo(() => {
    if (schema) return null;

    try {
      const value = typeof metadata === "string" ? JSON.parse(metadata || "{}") : metadata ?? {};

      if (!value || typeof value !== "object" || Object.keys(value).length === 0) return null;

      return inferSchema(value);
    } catch (error) {
      return null;
    }
  }, [schema, metadata]);

  const exported = schema ?? inferred;

  const json = useMemo(() => {
    if (!exported) return "";

    if (typeof exported === "string") {
      try {
        return JSON.stringify(JSON.parse(exported), null, 2);
      } catch (error) {
        return exported;
      }
    }

    return JSON.stringify(exported, null, 2);
  }, [exported]);

  if (!show) return null;

  const copy = async () => {
    if (!json) return;

    await copyToClipboard(json);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  const download = () => {
    if (!json) return;

    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const safeName = String(name ?? "dataset").replace(/[^a-z0-9_-]+/gi, "-").toLowerCase();

    link.href = url;
    link.download = `${safeName}-metadata-schema.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Header>
          <Title>
            <Icon icon="lucide:braces" /> Export schema
          </Title>
          <Button type="button" onClick={onClose} aria-label="Close">
            <Icon icon="lucide:x" /> Close
          </Button>
        </Header>

        <Hint>
          {inferred
            ? "This data set has no schema yet, so one was inferred from its metadata. Review it, then use it as the data set schema if it looks right."
            : "The item metadata schema stored on this data set. Copy it, download it, or reuse it elsewhere."}
        </Hint>

        {json ? (
          <Code readOnly spellCheck={false} value={json} />
        ) : (
          <Hint>There is no schema and no metadata to infer one from.</Hint>
        )}

        {applied ? <Notice>Set as the data set schema — Save Changes to persist it.</Notice> : null}

        <Row>
          <Button type="button" onClick={copy} disabled={!json}>
            <Icon icon={copied ? "lucide:check" : "lucide:copy"} /> {copied ? "Copied" : "Copy JSON"}
          </Button>

          <Button type="button" onClick={download} disabled={!json}>
            <Icon icon="lucide:download" /> Download
          </Button>

          {inferred && onUseSchema ? (
            <Primary
              type="button"
              onClick={() => {
                onUseSchema(inferred);
                setApplied(true);
              }}
            >
              <Icon icon="lucide:file-json" /> Use as data set schema
            </Primary>
          ) : null}
        </Row>
      </Panel>
    </Overlay>
  );
};

export default SchemaExportDialog;
